import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Text,
  Button,
  Card,
  Caption1 } from
'@fluentui/react-components';
import { ArrowRightRegular } from
'@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { teamsService } from '../services/teamsService';
import { gfcPolicyService } from '../services/gfcPolicyService';
import { domainService } from '../services/domainService';

const useStyles = makeStyles({
  dashboard: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px')
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0'),
    ...shorthands.margin('0', '0', '24px', '0')
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px'
  },
  cardsGrid: {
    display: 'flex',
    ...shorthands.gap('20px'),
    marginBottom: '24px'
  },
  statusCard: {
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('12px'),
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    ...shorthands.padding('20px'),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '160px',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
    }
  },
  alertCard: {
    backgroundColor: '#fff5f5',
    ...shorthands.border('1px', 'solid', '#ffdddd'),
    minWidth: '350px'
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px')
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px'
  },
  cardTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  cardValue: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    lineHeight: '1.2',
    marginTop: '8px',
    marginBottom: '4px'
  },
  alertValue: {
    color: '#dc3545'
  },
  cardDescription: {
    fontSize: '14px',
    color: '#495057',
    lineHeight: '1.4'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    ...shorthands.padding('4px', '12px'),
    ...shorthands.borderRadius('20px'),
    fontSize: '13px',
    fontWeight: '600'
  },
  connectedBadge: {
    backgroundColor: '#d4f4dd',
    color: '#1e7e34'
  },
  disconnectedBadge: {
    backgroundColor: '#ffeef0',
    color: '#dc3545'
  },
  viewButton: {
    marginTop: 'auto',
    alignSelf: 'flex-end'
  },
  domainUsageSection: {
    backgroundColor: '#f8f9fa',
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', '#e9ecef'),
    ...shorthands.padding('24px')
  },
  domainUsageHeader: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '20px'
  },
  domainUsageBar: {
    height: '8px',
    backgroundColor: '#e9ecef',
    ...shorthands.borderRadius('4px'),
    ...shorthands.overflow('hidden'),
    marginBottom: '16px'
  },
  domainUsageProgress: {
    height: '100%',
    backgroundColor: '#0047AB',
    transition: 'width 0.3s ease'
  },
  domainStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    ...shorthands.gap('16px'),
    marginTop: '20px'
  },
  domainStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('8px'),
    ...shorthands.border('1px', 'solid', '#dee2e6'),
    ...shorthands.padding('16px', '12px')
  },
  domainStatValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1a1a1a',
    display: 'block',
    marginBottom: '4px'
  },
  domainStatLabel: {
    fontSize: '12px',
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    lineHeight: '1.3'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('40px')
  }
});

interface DashboardState {
  teamsStatus: 'connected' | 'disconnected' | 'loading';
  lsegStatus: 'connected' | 'disconnected' | 'loading';
  approvalCount: number;
  domainCount: number;
  gfcPrerequisites: any;
  isLoading: boolean;
  domainUsage: {
    configured: number;
    allowed: number;
    blocked: number;
    pending: number;
    remaining: number;
  };
  lsegSyncInfo: {
    lastSync: string;
    domainCount: number;
  };
}

export const Dashboard: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [state, setState] = useState<DashboardState>({
    teamsStatus: 'loading',
    lsegStatus: 'loading',
    approvalCount: 5,
    domainCount: 8,
    gfcPrerequisites: null,
    isLoading: true,
    domainUsage: {
      configured: 0,
      allowed: 0,
      blocked: 0,
      pending: 0,
      remaining: 4000
    },
    lsegSyncInfo: {
      lastSync: '',
      domainCount: 0
    }
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      // Check Teams connection
      try {
        const teamsHealth = await teamsService.checkConnection();
        setState((prev) => ({
          ...prev,
          teamsStatus: teamsHealth.connected ? 'connected' : 'disconnected'
        }));
      } catch {
        setState((prev) => ({ ...prev, teamsStatus: 'disconnected' }));
      }

      // Check GFC prerequisites
      try {
        const prerequisites = await gfcPolicyService.checkGFCPrerequisites();
        setState((prev) => ({ ...prev, gfcPrerequisites: prerequisites }));
      } catch (error) {
        console.error('Failed to check GFC prerequisites:', error);
      }

      // Get Domain Feed domain sync info and domain counts
      try {
        const domains = await domainService.getDomains();
        const lsegDomains = domains.filter((d) => d.source === 'domain_feed');

        // Calculate domain statistics
        const allowedDomains = domains.filter((d) => d.approval_status === 'approved' || d.status === 'active');
        const blockedDomains = domains.filter((d) => d.status === 'suspended');
        const pendingDomains = domains.filter((d) => d.approval_status === 'pending');
        const configuredCount = allowedDomains.length + blockedDomains.length;

        // Find the most recent sync time
        let lastSyncTime = '';
        if (lsegDomains.length > 0) {
          const mostRecent = lsegDomains.reduce((latest, domain) => {
            return new Date(domain.created_at) > new Date(latest.created_at) ? domain : latest;
          }, lsegDomains[0]);
          lastSyncTime = mostRecent.created_at.toISOString();
        }

        setState((prev) => ({
          ...prev,
          lsegStatus: 'connected',
          lsegSyncInfo: {
            lastSync: lastSyncTime,
            domainCount: lsegDomains.length
          },
          domainUsage: {
            configured: configuredCount,
            allowed: allowedDomains.length,
            blocked: blockedDomains.length,
            pending: pendingDomains.length,
            remaining: 4000 - configuredCount
          },
          isLoading: false
        }));
      } catch (error) {
        console.error('Failed to fetch domain sync info:', error);
        setState((prev) => ({
          ...prev,
          lsegStatus: 'disconnected',
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const usagePercentage = (state.domainUsage.configured / 4000 * 100).toFixed(1);

  /* Commented out code - not currently used */
  // Helper function to format time difference
  // const formatTimeAgo = (dateString: string): string => {
  //   if (!dateString) return 'Never';

  //   const date = new Date(dateString);
  //   const now = new Date();
  //   const diffMs = now.getTime() - date.getTime();
  //   const diffMins = Math.floor(diffMs / 60000);

  //   if (diffMins < 1) return 'Just now';
  //   if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;

  //   const diffHours = Math.floor(diffMins / 60);
  //   if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  //   const diffDays = Math.floor(diffHours / 24);
  //   return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  // };

  return (
    <div className={styles.dashboard} data-testid="dashboard-container">
      <div className={styles.header} data-testid="dashboard-header">
        <Text className={styles.title} data-testid="dashboard-title">Dashboard</Text>
      </div>

      <div className={styles.cardsGrid} data-testid="dashboard-cards-grid">
        <Card className={mergeClasses(styles.statusCard, state.approvalCount > 0 && styles.alertCard)} data-testid="dashboard-card-pending-approvals">
          <div className={styles.cardContent} data-testid="dashboard-card-content">
            <Text className={styles.cardTitle} data-testid="dashboard-pending-approvals-text">Pending Approvals</Text>
            <Text className={mergeClasses(styles.cardValue, state.approvalCount > 0 && styles.alertValue)} data-testid="dashboard-pending-approvals-value">
              {state.approvalCount}
            </Text>
            <Text className={styles.cardDescription} data-testid="dashboard-new-od-members-need-approval-text">New OD members need approval</Text>
          </div>
          <Button
            appearance={state.approvalCount > 0 ? "primary" : "outline"}
            icon={<ArrowRightRegular data-testid="dashboard-arrow-right-regular" />}
            iconPosition="after"
            size="small"
            onClick={() => navigate('/approvals')}
            style={{ alignSelf: 'stretch' }} data-testid="dashboard-view-approvals-button">

            View Approvals
          </Button>
        </Card>

      </div>

      <Card className={styles.domainUsageSection} data-testid="dashboard-card-domain-usage">
        <Text className={styles.domainUsageHeader} data-testid="dashboard-tenant-wide-domain-usage-text">Tenant-wide Domain Usage</Text>
        <div className={styles.domainUsageBar} data-testid="dashboard-domain-usage-bar-div">
          <div
            className={styles.domainUsageProgress}
            style={{ width: `${usagePercentage}%` }} data-testid="dashboard-domain-usage-progress-div" />

        </div>
        <Caption1 data-testid="dashboard-4000-caption1">{state.domainUsage.configured}/4000</Caption1>
        
        <div className={styles.domainStats} data-testid="dashboard-domain-stats-div">
          <div className={styles.domainStat} data-testid="dashboard-domain-stat-div">
            <Text className={styles.domainStatValue} data-testid="dashboard-total-configured-value">{state.domainUsage.configured}</Text>
            <Text className={styles.domainStatLabel} data-testid="dashboard-total-configured-text">Total Configured</Text>
          </div>
          <div className={styles.domainStat} data-testid="dashboard-domain-stat-2-div">
            <Text className={styles.domainStatValue} data-testid="dashboard-allowed-domains-value">{state.domainUsage.allowed}</Text>
            <Text className={styles.domainStatLabel} data-testid="dashboard-allowed-domains-text">Allowed Domains</Text>
          </div>
          <div className={styles.domainStat} data-testid="dashboard-domain-stat-3-div">
            <Text className={styles.domainStatValue} data-testid="dashboard-blocked-domains-value">{state.domainUsage.blocked}</Text>
            <Text className={styles.domainStatLabel} data-testid="dashboard-blocked-domains-text">Blocked Domains</Text>
          </div>
          <div className={styles.domainStat} data-testid="dashboard-domain-stat-4-div">
            <Text className={styles.domainStatValue} data-testid="dashboard-pending-domains-value">{state.domainUsage.pending}</Text>
            <Text className={styles.domainStatLabel} data-testid="dashboard-pending-domains-text">Pending Domains</Text>
          </div>
          <div className={styles.domainStat} data-testid="dashboard-domain-stat-5-div">
            <Text className={styles.domainStatValue} data-testid="dashboard-remaining-capacity-value">{state.domainUsage.remaining}</Text>
            <Text className={styles.domainStatLabel} data-testid="dashboard-remaining-capacity-text">Remaining Capacity</Text>
          </div>
        </div>
      </Card>
    </div>);

};